// https://github.com/neilj/Squire

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import Squire from 'squire-rte';
import Bem from 'bemHelper';
import { UserProp } from 'auth';
import isArray from 'lodash/isArray';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import CodeEditor from 'containers/form/extended/input-code';
import Button from 'components/button';
import { iconTypes } from 'components/icon';
import CommonError from 'errors/commonError';

import createVideo from './video-parser';
import ModalEditor from './modal-editor';

import 'styles/base/containers/form/extended/editor-light/_editor-light.scss';

const classesEditor = new Bem('editorLight');
const modalContentCfg = {
  a: 'a',
  img: 'img',
  iframe: 'iframe',
  smiles: 'smiles'
};

@onClickOutside
export default class EditorLight extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line
    onChange: PropTypes.func,
    imagePath: PropTypes.string,
    isAdmin: PropTypes.bool,
    sendEnable: PropTypes.bool,
    autoFocus: PropTypes.bool,
    inForm: PropTypes.bool,
    auth: UserProp,
    actionDoLogin: PropTypes.func,
    onCancel: PropTypes.func,
    focusLogin: PropTypes.bool,
    submitOnEnter: PropTypes.bool,
    toolbar: PropTypes.bool,
    dataTest: PropTypes.string,
    castrated: PropTypes.bool,
  };

  static defaultProps = {
    imagePath: '/images/orig/full',
    autoFocus: false,
    toolbar: true,
    castrated: false
  };

  static createElement(element, props, inner) {
    const tag = document.createElement(element);
    if (props) forEach(props, (attr, key) => tag.setAttribute(key, attr));
    if (inner) tag.innerHTML = inner;
    return tag;
  }

  /**
   * Checks if value without `p` and `br` tags is empty
   *
   * @param value
   * @returns {boolean}
   */
  static checkValueIsEmpty(value) {
    if (!value) return !value;
    return !value.replace(/(<p>|<br>|<\/p>|&nbsp;)/g, '').trim();
  }

  constructor(props, ctx) {
    super(props, ctx);
    this.toggleModal = this.toggleModal.bind(this);
    this.createEditor = this.createEditor.bind(this);
    this.onAddNewRow = this.onAddNewRow.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onHandleFocus = this.onHandleFocus.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onHandleCursorPosition = this.onHandleCursorPosition.bind(this);
    this.onHandleCopy = this.onHandleCopy.bind(this);
    this.onHandlePaste = this.onHandlePaste.bind(this);
    this.toggleListeners = this.toggleListeners.bind(this);
    this.toggleBtnState = this.toggleBtnState.bind(this);

    this.onHandleClick = this.onHandleClick.bind(this);
    this.changeFormat = this.changeFormat.bind(this);
    this.insertSrc = this.insertSrc.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    this.editor = null;
    this.submitBtn = null;
    this.modalContent = null;
    // this.userChanges = false;
    this.loginAfterFocus = false;
    this.ctrlPressed = false;
    this.buttonsConfig = {
      b: {
        tag: 'b',
        icon: iconTypes.formatBold,
        toggleState: true,
        enableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('b')});
          this.editor.bold();
        },
        disableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('b', null, false)});
          this.editor.removeBold();
        }
      },
      i: {
        tag: 'i',
        icon: iconTypes.formatItalic,
        toggleState: true,
        enableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('i')});
          this.editor.italic();
        },
        disableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('i', null, false)});
          this.editor.removeItalic();
        }
      },
      u: {
        tag: 'u',
        icon: iconTypes.formatUnderline,
        toggleState: true,
        enableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('u')});
          this.editor.underline();
        },
        disableMethod: () => {
          this.setState({activeBtns: this.toggleBtnState('u', null, false)});
          this.editor.removeUnderline();
        }
      }
    }

    if (!props.castrated) {
      this.buttonsConfig = {
        ...this.buttonsConfig,
        ul: {
          tag: 'ul',
          icon: iconTypes.formatUnorderedList,
          toggleState: true,
          enableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('ul')});
            this.editor.makeUnorderedList();
          },
          disableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('ul', null, false)});
            this.editor.removeList();
          }
        },
        ol: {
          tag: 'ol',
          icon: iconTypes.formatOrderedList,
          toggleState: true,
          enableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('ol')});
            this.editor.makeOrderedList();
          },
          disableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('ol', null, false)});
            this.editor.removeList();
          }
        },
        s: {
          tag: 's',
          icon: iconTypes.formatStrike,
          toggleState: true,
          enableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('s')});
            this.changeFormat({tag: 's'});
          },
          disableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('s', null, false)});
            this.changeFormat(null, {tag: 's'});
          }
        },
        separation1: {tag: 'separation'},
        a: {
          tag: 'a',
          icon: iconTypes.copyLink,
          toggleState: true,
          enableMethod: () => {
            this.modalContent = modalContentCfg.a;
            this.toggleModal(this.toggleBtnState('a'));
          },
          disableMethod: () => {
            this.modalContent = null;
            this.editor.removeLink();
            this.setState({activeBtns: this.toggleBtnState('a', null, false)});
          }
        },
        blockquote: {
          tag: 'blockquote',
          icon: iconTypes.formatQuote,
          toggleState: true,
          enableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('blockquote')});
            this.editor.increaseQuoteLevel();
          },
          disableMethod: () => {
            this.setState({activeBtns: this.toggleBtnState('blockquote', null, false)});
            this.editor.decreaseQuoteLevel();
          }
        },
        separation2: {tag: 'separation'},
        img: {
          tag: 'img',
          icon: iconTypes.imageUndefined,
          enableMethod: () => {
            this.modalContent = modalContentCfg.img;
            this.toggleModal();
          },
          disableMethod: () => this.modalContent = null
        },
        iframe: {
          tag: 'iframe',
          icon: iconTypes.video,
          enableMethod: () => {
            this.modalContent = modalContentCfg.iframe;
            this.toggleModal();
          },
          disableMethod: () => this.modalContent = null
        }
      };
    }
    if (props.isAdmin) {
      this.buttonsConfig.separation3 = { tag: 'separation' };
      this.buttonsConfig.code = {
        tag: 'code',
        icon: iconTypes.codeSettings,
        enableMethod: () => {
          this.toggleListeners(true);
          this.editor.destroy();
          this.setState({ showCode: true, activeBtns: this.toggleBtnState('code') });
        },
        disableMethod: () =>
          this.setState(
            { showCode: false, activeBtns: this.toggleBtnState('code', null, false) },
            this.createEditor
          )
      };
    }

    this.state = {
      activeBtns: [],
      showModal: false,
      showCode: false
    };
  }

  componentDidMount() {
    this.createEditor();
  }

  componentDidUpdate(prevProps) {
    const { auth: prevAuth = {} } = prevProps;
    const { auth = {}, autoFocus, focusLogin } = this.props;

    if (!this._internalChange && !this.state.showCode && this.props.value !== prevProps.value) {
      this.editor.setHTML(this.props.value || '');
    }
    this._internalChange = false;

    if (
      (autoFocus || (focusLogin && this.loginAfterFocus)) &&
      // (!this.userChanges ||
      (prevAuth.authenticated !== auth.authenticated && auth.authenticated)
    ) {
      this.loginAfterFocus = false;
      this.editor.focus();
      this.editor.moveCursorToEnd();
      this.scrollToCaret();
    }
  }

  componentWillUnmount() {
    this.editor.destroy();

    this.toggleListeners(true);
  }

  onHandleFocus() {
    const { auth = {}, actionDoLogin } = this.props;
    if (!auth.authenticated && actionDoLogin) {
      this.editor.blur();
      this.loginAfterFocus = true;
      actionDoLogin();
    }
  }

  onChange() {
    this.scrollToCaret();
    this._internalChange = true;

    this.userChanges = true;
    this.props.onChange(this.editor.getHTML());
  }

  onSubmit() {
    if (this.submitBtn) {
      this.submitBtn.click();
    }
  }

  onAddNewRow(self, event, range) {
    return this.editor._keyHandlers.enter(self, event, range);
  }

  onKeyDown(e) {
    // Ctrl key pressed down
    if (e.keyCode === 17) {
      this.ctrlPressed = true;
    }

    if (this.props.submitOnEnter && !this.ctrlPressed && e.keyCode === 13) {
      e.preventDefault();
      this.onSubmit();
    }

    if (!this.props.submitOnEnter && this.ctrlPressed && e.keyCode === 13) {
      e.preventDefault();
      this.onSubmit();
    }
  }

  onKeyUp(e) {
    // Ctrl key pressed up
    if (e.keyCode === 17) {
      this.ctrlPressed = false;
    }
  }

  /**
   * Fires on copying elements inside editor
   * @param event
   */
  onHandleCopy(event) {
    try {
      document.execCommand('copy');
      event.preventDefault();
    } catch (e) {
      return false;
    }
  }

  /**
   * Gets fragment from event with the html to paste. Modifies and rewrites it.
   * @param event
   */
  onHandlePaste(event) {
    try {
      // Get content from document fragment
      const div = document.createElement('div');
      div.appendChild(event.fragment.cloneNode(true));
      let html = div.innerHTML;

      // clean up html before pasting it in
      import('sanitize-html').then((mod) => {
        const sanitizeHtml = mod.default;

        html = sanitizeHtml(html, {
          allowedTags: ['img', 'blockquote', 'p', 'a', 'ul', 'ol', 'nl', 'li', 'b', 'i', 'strong', 'em', 'strike', 's', 'u', 'code', 'hr', 'br', 'div', 'pre', 'iframe'],
          // allow only 'href' and 'src' attributes
          allowedAttributes: {
            '*': ['href', 'src']
          },
          // deny any meta and empty tags
          exclusiveFilter: (frame) => {
            if (frame.tag === 'meta') return false;
            return !frame.text.trim();
          }
        });

        // eslint-disable-next-line
        event.fragment = document.createRange().createContextualFragment(html);
      });
    } catch (e) {
      throw new CommonError(e);
    }
  }

  /**
   * Fires each time cursor position is changed
   * (for detecting which button actions has been applied)
   */
  onHandleCursorPosition() {
    let activeBtns = [];
    forEach(this.buttonsConfig, ({ tag, toggleState }) => {
      if (toggleState) {
        activeBtns = this.toggleBtnState(tag, activeBtns, this.editor.hasFormat(tag));
      }
    });
    this.setState({ activeBtns });
  }

  /**
   * Fires on button click. Handle both applying and canceling actions.
   * @param key
   */
  onHandleClick(key) {
    if (!key || (key !== 'code' && this.state.showCode)) return null;

    const btn = this.buttonsConfig[key];
    if ((key === 'code' && this.state.showCode) || this.editor.hasFormat(key)) {
      btn.disableMethod();
    } else {
      btn.enableMethod();
    }
  }

  handleClickOutside() {
    if (
      this.props.onCancel && EditorLight.checkValueIsEmpty(this.props.value)
    ) {
      this.props.onChange('');
      this.props.onCancel();
    }
  }

  /**
   * Toggle btn in state array
   *
   * @param btn
   * @param activeArr
   * @param add
   * @returns {[]}
   */
  toggleBtnState(btn = null, activeArr = null, add = true) {
    const activeBtns = activeArr ? [...activeArr] : [...this.state.activeBtns];
    if (!btn) return activeBtns;

    const index = activeBtns.indexOf(btn);
    if (add && index < 0) {
      activeBtns.push(btn);
    }
    if (!add && index >= 0) {
      activeBtns.splice(index, 1);
    }
    return activeBtns;
  }

  toggleListeners(remove = false) {
    if (remove) {
      // Remove event listeners and copy event
      this.editor.setKeyHandler('ctrl-c', null);
      this.editor.removeEventListener('cursor', this.onHandleCursorPosition);
      this.editor.removeEventListener('input', this.onChange);
      this.editor.removeEventListener('willPaste', this.onHandlePaste);
      this.editor.removeEventListener('focus', this.onHandleFocus);
      if (this.props.submitOnEnter) {
        this.editor.setKeyHandler('ctrl-enter', null);
        this.editor.removeEventListener('keydown', this.onKeyDown);
        this.editor.removeEventListener('keyup', this.onKeyUp);
      }
    } else {
      // Add event listeners and copy event
      this.editor.setKeyHandler('ctrl-c', this.onHandleCopy);
      this.editor.addEventListener('cursor', this.onHandleCursorPosition);
      this.editor.addEventListener('input', this.onChange);
      this.editor.addEventListener('willPaste', this.onHandlePaste);
      this.editor.addEventListener('focus', this.onHandleFocus);
      this.editor.addEventListener('keydown', this.onKeyDown);
      this.editor.addEventListener('keyup', this.onKeyUp);
      if (this.props.submitOnEnter) {
        this.editor.setKeyHandler('ctrl-enter', this.onAddNewRow);
      }
    }
  }

  toggleModal(linkArr) {
    this.setState(state => ({
      showModal: !state.showModal,
      activeBtns: linkArr || state.activeBtns
    }));
  }

  changeFormat(tagAdd = null, tagRemove = null) {
    this.editor.changeFormat(tagAdd, tagRemove);
    this.editor.focus();
  }

  /**
   * Fires after OK is cliked in modal. Inserts link/image/video in editor by it's src.
   * @param src
   */
  insertSrc(src) {
    if (!src) return null;

    // insert anchor link
    if (this.modalContent === modalContentCfg.a) {
      this.modalContent = null;
      return this.editor.makeLink(src);
    }

    // insert video iframe
    if (this.modalContent === modalContentCfg.iframe) {
      const ifrm = createVideo(src, '100%', '100%');
      this.modalContent = null;
      this.editor.insertHTML(ifrm);
      return this.editor.focus();
    }

    // insert image
    if (this.modalContent === modalContentCfg.img) {
      this.modalContent = null;
      const { imagePath } = this.props;
      const attr = { class: 'postImg', alt: '' };

      // multiple images uploaded
      if (isArray(src)) {
        if (src.length === 1) return this.editor.insertImage(`${imagePath}/${src[0].payload.filename}`, attr);

        const div = EditorLight.createElement('div', { class: 'attachesBlock' });
        forEach(src, (it) => {
          if (it.payload.filename) {
            const img = EditorLight.createElement('img', { ...attr, src: `${imagePath}/${it.payload.filename}` });
            div.appendChild(img);
          }
        });
        return this.editor.insertHTML(div.outerHTML);
      }
      // single image uploaded
      if (src.filename) return this.editor.insertImage(`${imagePath}/${src.filename}`, attr);
      // user just inserted link on image
      return this.editor.insertImage(src, attr);
    }

    // insert smiles
    if (this.modalContent === modalContentCfg.smiles) {
      forEach(src, it => this.editor.insertImage(it));
    }
  }

  createEditor() {
    this.editor = new Squire(this.elementRef, {
      blockTag: 'p'
    });

    this.editor.setHTML(this.props.value || '');

    this.toggleListeners();

    if (this.props.autoFocus) {
      this.editor.focus();
      this.editor.moveCursorToEnd();
      this.scrollToCaret();
    }
  }

  scrollToCaret() {
    // Scrolls to caret, prevents caret from been scrolled over
    const caretBottom = this.editor.getCursorPosition().bottom;
    const editorBottom = this.elementRef.getBoundingClientRect().bottom;
    if (caretBottom > editorBottom) {
      this.elementRef.scrollTop += caretBottom - editorBottom;
    }
  }

  renderToolbar() {
    return (
      <div {...classesEditor('toolbar')}>
        {map(this.buttonsConfig, (it, index) => {
          if (it.tag === 'separation') return <span key={index} {...classesEditor('separate')} />;

          return (
            <Button
              key={it.tag}
              {...classesEditor(
                'btn',
                {
                  toolbar: true,
                  active: this.state.activeBtns.includes(it.tag) || (it.tag === 'code' && this.state.showCode)
                },
              )}
              onClick={() => this.onHandleClick(it.tag)}
              iconType={it.icon}
              iconOnly
            />
          );
        })}
      </div>
    );
  }

  renderEditorContent() {
    if (this.state.showCode) return <CodeEditor {...this.props} lang="javascript" />;

    return (
      <div {...classesEditor('editor')}>
        <div {...classesEditor('editorContent')} ref={el => this.elementRef = el} data-test="editorContent" />
        <Button
          {...classesEditor('btn', 'emoji')}
          iconType={iconTypes.iconEmoji}
          onClick={() => {
            this.modalContent = modalContentCfg.smiles;
            this.toggleModal();
          }}
        />
        {this.props.sendEnable &&
          <Button
            {...classesEditor('btn', 'submit')}
            btnRef={node => this.submitBtn = node}
            type="submit"
            iconType={iconTypes.send}
            noPadding
          />
        }
      </div>
    );
  }

  render() {
    if (__SERVER__) return <div />;

    const { className, inForm, dataTest, toolbar } = this.props;

    return (
      <div
        {...classesEditor({
          modifiers: inForm && 'inForm',
          extra: className
        })}
        data-test={dataTest && `editorContent__${dataTest}`}
      >
        {toolbar && this.renderToolbar()}
        {this.renderEditorContent()}
        {this.state.showModal &&
          <ModalEditor
            onCloseRequired={() => {
              if (this.modalContent === 'a') {
                this.modalContent = null;
                this.toggleModal(this.toggleBtnState('a', null, false));
                return this.editor.focus();
              }
              this.toggleModal();
              this.editor.focus();
            }}
            content={this.modalContent}
            insertSrc={this.insertSrc}
          />
        }
      </div>
    );
  }
}
